<script lang="ts" setup>
import { ChevronDown, ChevronRight, Users } from 'lucide-vue-next';
import { ref } from 'vue';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { Member, Organization } from '@/stores/access-management';
import { useAccessManagementStore } from '@/stores/access-management';

import InviteMemberDialog from './dialogs/InviteMemberDialog.vue';
import MembersTable from './MembersTable.vue';

interface Props {
  organization: Organization;
  currentUserId?: string;
}

const props = defineProps<Props>();

const accessStore = useAccessManagementStore();

const isExpanded = ref(true);

function toggleExpanded() {
  isExpanded.value = !isExpanded.value;
}

async function handleRoleUpdate(member: Member, newRole: string) {
  await accessStore.updateMemberRole(props.organization.orgId, member.userId, newRole);
}

async function handleMemberRemove(member: Member) {
  await accessStore.removeMember(props.organization.orgId, member.userId);
}
</script>

<template>
  <Card>
    <CardHeader>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <button
            @click="toggleExpanded"
            class="hover:bg-muted rounded p-1 transition-colors"
          >
            <ChevronRight v-if="!isExpanded" class="h-5 w-5" />
            <ChevronDown v-else class="h-5 w-5" />
          </button>
          <div>
            <CardTitle class="text-xl">{{ organization.orgName }}</CardTitle>
            <CardDescription>
              {{ organization.members.length }} member{{ organization.members.length !== 1 ? 's' : '' }} (including you)
              • Your role: {{ organization.userRole }}
            </CardDescription>
          </div>
        </div>

        <InviteMemberDialog
          :org-id="organization.orgId"
          :org-name="organization.orgName"
        />
      </div>
    </CardHeader>

    <CardContent v-if="isExpanded" class="pt-0">
      <div class="space-y-4">
        <div>
          <h3 class="mb-3 text-sm font-medium flex items-center gap-2">
            <Users class="h-4 w-4" />
            Members
          </h3>

          <MembersTable
            :members="organization.members"
            :org-id="organization.orgId"
            :current-user-id="currentUserId"
            @role-updated="handleRoleUpdate"
            @member-removed="handleMemberRemove"
          />
        </div>
      </div>
    </CardContent>
  </Card>
</template>
